import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Users, Globe, AlertTriangle, RefreshCw } from "lucide-react";

export default function AdminVideoStats() {
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/video-stats");
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  if (!stats) return <div className="mt-10 text-ink/40 text-sm">Chargement…</div>;

  return (
    <div className="mt-10 space-y-10" data-testid="admin-video-stats">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink/55 max-w-2xl">
          Chaque lecture de vidéo est enregistrée (client, adresse IP, date). Si un client regarde depuis
          <strong> 3 adresses IP différentes ou plus</strong> (ou plus de 15 lectures) sur une même vidéo,
          il apparaît dans les alertes — signe probable que son compte ou le lien a été partagé.
          Vous pouvez alors révoquer son accès (onglet Utilisateurs) et/ou remplacer le lien YouTube de la vidéo (onglet Formations).
        </p>
        <button onClick={load} data-testid="stats-refresh" className="shrink-0 border border-ink/20 hover:border-ember hover:text-ember text-ink/60 p-2.5 transition-colors" title="Rafraîchir">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* FLAGGED */}
      <div>
        <h2 className="font-display font-medium text-lg text-ink flex items-center gap-2">
          <AlertTriangle size={17} className="text-amber-600" /> Alertes de partage ({stats.flagged.length})
        </h2>
        <div className="mt-4 border border-ink/10 bg-surface">
          {stats.flagged.length === 0 ? (
            <p className="px-6 py-8 text-sm text-green-600" data-testid="no-flags">Aucun partage suspect détecté.</p>
          ) : (
            stats.flagged.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-ink/5" data-testid={`flag-${i}`}>
                <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-medium text-ink">{f.email}</p>
                  <p className="text-xs text-ink/45">{f.lesson_title}</p>
                </div>
                <span className="text-xs text-ink/60">{f.plays} lectures</span>
                <span className="text-xs font-medium text-amber-600">{f.unique_ips} IP différentes</span>
                <span className="text-xs text-ink/40">dernière : {new Date(f.last_seen).toLocaleString()}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* PER LESSON */}
      <div>
        <h2 className="font-display font-medium text-lg text-ink">Statistiques par vidéo</h2>
        <div className="mt-4 border border-ink/10 bg-surface overflow-x-auto">
          <div className="min-w-[560px]">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-ink/10 text-[11px] uppercase tracking-[0.2em] text-ink/40">
              <span className="col-span-6">Vidéo</span>
              <span className="col-span-2 text-center"><Eye size={13} className="inline" /> Lectures</span>
              <span className="col-span-2 text-center"><Users size={13} className="inline" /> Clients</span>
              <span className="col-span-2 text-center"><Globe size={13} className="inline" /> IP</span>
            </div>
            {stats.lessons.length === 0 && <p className="px-6 py-8 text-sm text-ink/40">Aucune lecture enregistrée pour l'instant.</p>}
            {stats.lessons.map((l) => (
              <div key={l.lesson_id} className="grid grid-cols-12 gap-4 px-6 py-3.5 border-b border-ink/5 text-sm items-center" data-testid={`stat-lesson-${l.lesson_id}`}>
                <span className="col-span-6 text-ink/80 truncate">{l.title}</span>
                <span className="col-span-2 text-center font-medium text-ink">{l.plays}</span>
                <span className="col-span-2 text-center text-ink/60">{l.unique_users}</span>
                <span className="col-span-2 text-center text-ink/60">{l.unique_ips}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
